import { useAuth0 } from '@auth0/auth0-react';
import { Center, Loader, Notifications } from '@qred/components-library';
import * as Sentry from '@sentry/react';
import posthog from 'posthog-js';
import React, { useEffect, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useSelector } from 'react-redux';
import { RouteComponentProps, withRouter } from 'react-router';
import { CountryCode } from '~/enums';
import { useAppDispatch } from '~/hooks/useAppDispatch';
import { accessToken, TOKEN } from '~/store/apis/utils/accessToken';
import ErrorFallback from './components/shared/ErrorFallback/ErrorFallback';
import GlobalErrorPopup from './components/shared/GlobalErrorPopup/GlobalErrorPopup';
import HelpPopup from './components/shared/HelpPopup/HelpPopup';
import HubspotChat from './components/shared/HubspotChat/HubspotChat';
import ScrollToTop from './components/shared/ScrolToTop/ScrolToTop';
import constVars from './constants/constVars';
import { logToSentry } from './helpers/loggers.helper';
import { getAuth0Audience, getCookieValue } from './helpers/utils';
import { useAppInitEffects } from './hooks/useAppInitEffects';
import useLogout from './hooks/useLogout';
import Routes from './routes';
import { RootState } from './store';
import { updateMatchedMedia } from './store/slices/matchedMedia.slice';
import { setUser } from './store/slices/user.slice';
import GlobalStyle from './styles/index.css';

const Main: React.FC<RouteComponentProps<any>> = () => {
  const { market } = useSelector((state: RootState) => state.intl);
  const {
    isAuthenticated,
    isLoading,
    getAccessTokenSilently,
    loginWithRedirect,
  } = useAuth0();
  const dispatch = useAppDispatch();
  const logout = useLogout();
  const [
    authenticationStatusHandled,
    setAuthenticationStatusHandled,
  ] = useState(false);
  useAppInitEffects();

  const onError = (error: Error) => {
    logToSentry(error, 'Uncaught error in Main.tsx');
  };

  useEffect(() => {
    if (market === CountryCode.NL) {
      document.title = 'Qeld | My Qeld';
    }

    return () => {
      document.title = 'Qred | My Qred';
    };
  }, [market]);

  const identifyPostHogAndSentryUser = (userQCode: string) => {
    try {
      if (userQCode) {
        posthog.identify(userQCode);
        Sentry.setUser({ id: userQCode });
      }
    } catch (error) {
      console.error('Error identifying user in PostHog or Sentry:', error);
    }
  };

  useEffect(() => {
    const handleAuthenticationStatus = async () => {
      try {
        posthog.setPersonProperties({
          isAuthenticated,
        });

        if (isAuthenticated) {
          const _accessToken = await getAccessTokenSilently({
            authorizationParams: {
              audience: getAuth0Audience(),
            },
          });
          accessToken.set(_accessToken);
          const decodedAccessToken = accessToken.getDecoded();
          const userQCode = decodedAccessToken[TOKEN.USER_CODE];

          identifyPostHogAndSentryUser(userQCode);
          dispatch(
            setUser({
              isAuthenticated: true,
              authToken: _accessToken,
            })
          );
          localStorage.setItem('isAuthenticated', '1');
          document.cookie = 'publicToken=; Max-Age=0';
        } else {
          dispatch(
            setUser({
              isAuthenticated: false,
              authToken: getCookieValue('publicToken'),
            })
          );
          localStorage.removeItem('isAuthenticated');
        }
        setAuthenticationStatusHandled(true);
      } catch (error) {
        console.error('Error handling authentication status:', error);
        if (isAuthenticated) {
          logout(constVars.ROUTE_LOGIN);
        } else {
          setAuthenticationStatusHandled(true);
        }
      }
    };

    if (!isLoading) {
      if (!isAuthenticated && localStorage.getItem('isAuthenticated')) {
        loginWithRedirect({
          authorizationParams: {
            prompt: 'none',
          },
          appState: {
            returnTo: `${window.location.pathname}${window.location.search}`,
          },
        });
      } else {
        handleAuthenticationStatus();
      }
    }
  }, [isAuthenticated, isLoading]);

  const handleResize = () => {
    dispatch(updateMatchedMedia());
    document.documentElement.style.setProperty(
      '--client-height',
      `${window.innerHeight}px`
    );
  };

  useEffect(() => {
    dispatch(updateMatchedMedia());
    window.addEventListener('resize', handleResize);
    document.documentElement.style.setProperty(
      '--client-height',
      `${window.innerHeight}px`
    );
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  if (!authenticationStatusHandled) {
    return (
      <Center>
        <Loader size={'xxl'} />
      </Center>
    );
  }

  return (
    <React.StrictMode>
      <ErrorBoundary onError={onError} fallback={<ErrorFallback />}>
        <Notifications />
        <GlobalStyle />
        <ScrollToTop />
        <GlobalErrorPopup />
        <HelpPopup />
        <HubspotChat />
        <Routes />
      </ErrorBoundary>
    </React.StrictMode>
  );
};

export default withRouter(Main);
